import { Alert, Button, Flex, Input, Select, Space, Typography } from 'antd';
import { FC, memo } from 'react';
import { ArrowDown, LightningChargeFill } from 'react-bootstrap-icons';

import { DomainVisitors } from '@/modules/Domains';
import { PageNode } from '@/modules/Pages';
import { RedirectEndpointEnum, RedirectEndpointType, RedirectMatchMode } from '@/modules/Sites/Redirects';

import { RedirectPageFromAutocomplete } from '../../RedirectsForm/RedirectPageFromAutocomplete';
import { RedirectsConfig } from '../RedirectsConfig';
import { RedirectsBulkPagesPicker } from './RedirectsBulkPagesPicker';
import { RedirectsBulkTargetPreviewList } from './RedirectsBulkTargetPreviewList';

interface Props {
  siteId: string;
  pageItems: Pick<PageNode, 'id' | 'path'>[] | undefined;
  totalItems: number;
  currentPage: number;
  isPagesPathsLoading: boolean;
  search: string;
  selectedIds: string[];
  targetType: RedirectEndpointType;
  targetNodeId: string | undefined;
  pathTo: string;
  targetLabel: string;
  isTargetValid: boolean;
  statusCode: number;
  matchMode: RedirectMatchMode;
  forVisitor: DomainVisitors;
  isActive: boolean;
  items: Pick<PageNode, 'id' | 'path'>[];
  skippedSelfCount: number;
  isLoading: boolean;
  onChangeSearch: (value: string) => void;
  onChangePage: (page: number) => void;
  onToggleNode: (node: Pick<PageNode, 'id' | 'path'>, checked: boolean) => void;
  onToggleSelectAllOnPage: (checked: boolean) => void;
  onChangeTargetType: (value: RedirectEndpointType) => void;
  onChangeTargetNode: (node: Pick<PageNode, 'id' | 'path'> | undefined) => void;
  onChangePathTo: (value: string) => void;
  onChangeStatusCode: (value: number) => void;
  onChangeMatchMode: (value: RedirectMatchMode) => void;
  onChangeForVisitor: (value: DomainVisitors) => void;
  onChangeIsActive: (value: boolean) => void;
  onSubmit: () => void;
}

export const RedirectsBulkByPagesView: FC<Props> = memo(({
  siteId,
  pageItems,
  totalItems,
  currentPage,
  isPagesPathsLoading,
  search,
  onChangeSearch,
  onChangePage,
  selectedIds,
  onToggleNode,
  onToggleSelectAllOnPage,
  targetType,
  onChangeTargetType,
  targetNodeId,
  onChangeTargetNode,
  pathTo,
  onChangePathTo,
  targetLabel,
  isTargetValid,
  statusCode,
  onChangeStatusCode,
  matchMode,
  onChangeMatchMode,
  forVisitor,
  onChangeForVisitor,
  isActive,
  onChangeIsActive,
  items,
  skippedSelfCount,
  isLoading,
  onSubmit,
}) => {
  return (
    <Flex vertical gap={ 16 }>
      <Alert
        type="info"
        showIcon
        message="Оберіть сторінки, які треба спрямувати на одну ціль"
        description={
          <Typography.Text type="secondary">
            Позначте чекбоксами одну або кілька існуючих сторінок сайту та вкажіть спільну ціль «До» —
            існуючу сторінку сайту (переживає зміну слага) або довільний шлях. Для кожної обраної сторінки
            буде створено окремий редірект на вказану ціль.
          </Typography.Text>
        }
      />

      <RedirectsConfig
        forDomainVisitor={ forVisitor }
        active={ isActive }
        statusCode={ statusCode }
        matchMode={ matchMode }
        onChangeDomainVisitor={ onChangeForVisitor }
        onChangeActivity={ onChangeIsActive }
        onChangeStatusCode={ onChangeStatusCode }
        onChangeMatchMode={ onChangeMatchMode }
        disabled={ isLoading }
      />

      <Space size={ 4 } direction="vertical" style={{ width: '100%' }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={ 8 }>
          <Typography.Text type="secondary">З (сторінки сайту)</Typography.Text>
          <Typography.Text type="secondary">Обрано: <b>{ selectedIds.length }</b></Typography.Text>
        </Flex>

        <RedirectsBulkPagesPicker
          pageItems={ pageItems }
          totalItems={ totalItems }
          currentPage={ currentPage }
          isPagesPathsLoading={ isPagesPathsLoading }
          search={ search }
          onChangeSearch={ onChangeSearch }
          onChangePage={ onChangePage }
          selectedIds={ selectedIds }
          onToggleNode={ onToggleNode }
          onToggleSelectAllOnPage={ onToggleSelectAllOnPage }
          disabled={ isLoading }
        />
      </Space>

      <Flex align="center" justify="center">
        <ArrowDown size={ 30 }/>
      </Flex>

      <Space size={ 6 } direction="vertical" style={{ width: '100%' }}>
        <Typography.Text type="secondary">До — тип цілі</Typography.Text>
        <Select
          style={{ width: 200 }}
          value={ targetType }
          disabled={ isLoading }
          onChange={ onChangeTargetType }
          options={ [
            { value: RedirectEndpointEnum.Node, label: 'Сторінка (нода)' },
            { value: RedirectEndpointEnum.Path, label: 'Шлях' },
          ] }
        />

        { targetType === RedirectEndpointEnum.Node ? (
          <RedirectPageFromAutocomplete
            siteId={ siteId }
            value={ targetNodeId }
            onSelectValue={ onChangeTargetNode }
            disabled={ isLoading }
            allowClear
          />
        ) : (
          <Input
            value={ pathTo }
            onChange={ (event) => onChangePathTo(event.target.value) }
            placeholder="/target-page"
            disabled={ isLoading }
            allowClear
            status={ pathTo && !isTargetValid ? 'error' : undefined }
          />
        ) }
        { targetType === RedirectEndpointEnum.Path && pathTo && !isTargetValid && (
          <Typography.Text type="danger">Шлях має починатися з «/» і бути коректним</Typography.Text>
        ) }
      </Space>

      <RedirectsBulkTargetPreviewList items={ items } targetLabel={ targetLabel }/>

      <Flex justify="space-between" align="center" wrap="wrap" gap={ 8 }>
        <Space size={ 16 }>
          <Typography.Text type="secondary">Обрано сторінок: <b>{ selectedIds.length }</b></Typography.Text>
          <Typography.Text type="success">До створення: <b>{ items.length }</b></Typography.Text>
          { skippedSelfCount > 0 && (
            <Typography.Text type="warning">Пропущено (З = До): <b>{ skippedSelfCount }</b></Typography.Text>
          ) }
        </Space>

        <Button
          type="primary"
          icon={ <LightningChargeFill/> }
          loading={ isLoading }
          disabled={ items.length === 0 }
          onClick={ onSubmit }
        >
          Створити
        </Button>
      </Flex>
    </Flex>
  );
});
